// src/contexts/loginContext.jsx
// Autenticación y autorización basadas en RTDB /EmployeeID.
// Solo tienen acceso los correos que estén dentro de EmployeeID/hr.
import React, { createContext, useContext, useEffect, useState } from "react";
import {
  auth,
  signInWithGooglePopup,
  signOut as firebaseSignOut,
  dbAsistencia,
  signInWithEmailPass,
  setSessionPersistence,
} from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { ref, get } from "firebase/database";
import banner from "../assets/banner.png";

const LoginContext = createContext(null);
export const useAuth = () => useContext(LoginContext);

const STORAGE_KEY = "myapp_user_manual";
const REMEMBER_SESSION_KEY = "myapp_remember_session";
const NOT_ALLOWED_MSG =
  "ESTE USUARIO NO ESTÁ HABILITADO PARA USAR EL PANEL WEB DE ASISTENCIA";

function shouldRememberSession() {
  try {
    return localStorage.getItem(REMEMBER_SESSION_KEY) === "true";
  } catch {
    return false;
  }
}

function mapHrKeyToRole(hrKey) {
  if (!hrKey) {
    return { role: "basic", managerCode: null, employeeId: null };
  }

  if (hrKey === "0") {
    return {
      role: "admin",
      managerCode: null,
      employeeId: 0,
    };
  }

  const n = parseInt(hrKey, 10);
  if (Number.isFinite(n)) {
    return {
      role: "manager",
      managerCode: `manager${n}`,
      employeeId: n,
    };
  }

  return { role: "basic", managerCode: null, employeeId: null };
}

async function getRoleInfoFromEmployeeID(emailLower) {
  const snap = await get(ref(dbAsistencia, "EmployeeID"));
  if (!snap.exists()) {
    throw new Error(NOT_ALLOWED_MSG);
  }

  const data = snap.val() || {};
  const hrNode = data.hr || {};
  const vsNode = data.vs || {};
  const secNode = data.sec || {};

  for (const [subKey, subVal] of Object.entries(hrNode)) {
    if (typeof subVal === "string") {
      const stored = subVal.trim().toLowerCase();
      if (stored === emailLower) {
        const mapped = mapHrKeyToRole(subKey);
        return {
          role: mapped.role,
          managerCode: mapped.managerCode,
          employeeId: mapped.employeeId,
          hrKey: subKey,
          scope: "hr",
          vsKey: null,
        };
      }
    }
  }

  for (const [subKey, subVal] of Object.entries(vsNode)) {
    if (typeof subVal === "string") {
      const stored = subVal.trim().toLowerCase();
      if (stored === emailLower) {
        return {
          role: "viewer",
          managerCode: null,
          employeeId: null,
          hrKey: null,
          scope: "vs",
          vsKey: subKey,
        };
      }
    }
  }

  // Secretaría: solo accede a "Boletas firmadas".
  for (const subVal of Object.values(secNode)) {
    if (typeof subVal === "string") {
      const stored = subVal.trim().toLowerCase();
      if (stored === emailLower) {
        return {
          role: "secretaria",
          managerCode: null,
          employeeId: null,
          hrKey: null,
          scope: "sec",
          vsKey: null,
        };
      }
    }
  }

  throw new Error(NOT_ALLOWED_MSG);
}

async function getManagerDesignation(managerCode) {
  if (!managerCode) return "";
  const snap = await get(ref(dbAsistencia, `managers/${managerCode}/designation`));
  return snap.exists() ? String(snap.val() || "").trim() : "";
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const raw = shouldRememberSession() && localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [initializing, setInitializing] = useState(true);
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      let storedUser = null;
      try {
        const raw = shouldRememberSession() && localStorage.getItem(STORAGE_KEY);
        storedUser = raw ? JSON.parse(raw) : null;
      } catch {}

      try {
        if (!firebaseUser) {
          if (storedUser) {
            setUser(storedUser);
            setAuthError(null);
            setInitializing(false);
            return;
          }

          setUser(null);
          setAuthError(null);
          setInitializing(false);
          return;
        }

        const emailLower = (firebaseUser.email || "").trim().toLowerCase();
        if (!emailLower) {
          await firebaseSignOut().catch(() => {});
          setUser(null);
          setAuthError(NOT_ALLOWED_MSG);
          setInitializing(false);
          return;
        }

        const roleInfo = await getRoleInfoFromEmployeeID(emailLower);
        const designation =
          roleInfo.role === "manager"
            ? await getManagerDesignation(roleInfo.managerCode).catch(() => "")
            : "";

        const userData = {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL || null,
          provider: firebaseUser.providerData?.[0]?.providerId || "firebase",
          role: roleInfo.role,
          managerCode: roleInfo.managerCode,
          designation,
          employeeId: roleInfo.employeeId,
          hrKey: roleInfo.hrKey,
          scope: roleInfo.scope,
          vsKey: roleInfo.vsKey,
        };

        try {
          if (shouldRememberSession()) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(userData));
          } else {
            localStorage.removeItem(STORAGE_KEY);
          }
        } catch {}

        setUser(userData);
        setAuthError(null);
      } catch (err) {
        await firebaseSignOut().catch(() => {});
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch {}
        setUser(null);
        setAuthError(err?.message || NOT_ALLOWED_MSG);
      } finally {
        setInitializing(false);
      }
    });

    return () => unsub();
  }, []);

  const manualLogin = async (emailInput, password, remember = false) => {
    setLoading(true);
    setAuthError(null);
    try {
      await setSessionPersistence(remember);
      try {
        if (remember) {
          localStorage.setItem(REMEMBER_SESSION_KEY, "true");
        } else {
          localStorage.removeItem(REMEMBER_SESSION_KEY);
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch {}

      const rawEmail = (emailInput || "").trim();
      const emailLower = rawEmail.toLowerCase();

      if (!rawEmail) throw new Error("Ingresa un email.");
      if (!password) throw new Error("Ingresa una contraseña.");

      const fbUser = await signInWithEmailPass(rawEmail, password);

      try {
        await getRoleInfoFromEmployeeID(emailLower);
      } catch (authzErr) {
        await firebaseSignOut().catch(() => {});
        throw authzErr;
      }

      return fbUser;
    } catch (err) {
      const code = err?.code || "";
      if (
        code === "auth/invalid-credential" ||
        code === "auth/wrong-password" ||
        code === "auth/user-not-found" ||
        code === "auth/invalid-login-credentials"
      ) {
        setAuthError("Credenciales incorrectas.");
      } else {
        setAuthError(err?.message || NOT_ALLOWED_MSG);
      }
      throw err;
    } finally {
      setLoading(false);
      setInitializing(false);
    }
  };

  const loginWithGoogle = async (remember = false) => {
    setLoading(true);
    setAuthError(null);
    try {
      await setSessionPersistence(remember);
      try {
        if (remember) {
          localStorage.setItem(REMEMBER_SESSION_KEY, "true");
        } else {
          localStorage.removeItem(REMEMBER_SESSION_KEY);
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch {}

      const fbResult = await signInWithGooglePopup();
      return fbResult;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    setLoading(true);
    try {
      try {
        await firebaseSignOut();
      } catch {}
      try {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(REMEMBER_SESSION_KEY);
      } catch {}
      // Borra la conversación del asistente RAG para que otro usuario de la PC no la vea
      try {
        window.RagWidget?.olvidarTodo();
      } catch {}
      setUser(null);
      setAuthError(null);
    } finally {
      setLoading(false);
      setInitializing(false);
    }
  };

  const value = {
    user,
    loading,
    initializing,
    authError,
    manualLogin,
    loginWithGoogle,
    logout,
    isAuthenticated: !!user,
    role: user?.role || "basic",
    managerCode: user?.managerCode || null,
    designation: user?.designation || "",
    employeeId: user?.employeeId ?? null,
    hrKey: user?.hrKey ?? null,
    scope: user?.scope || null,
    vsKey: user?.vsKey ?? null,
  };

  if (initializing) {
    return (
      <div className="auth-loading" role="status" aria-live="polite">
        <img src={banner} alt="Centro Guaman Poma de Ayala" />
        <span>Verificando sesión…</span>
      </div>
    );
  }

  return <LoginContext.Provider value={value}>{children}</LoginContext.Provider>;
}
